import { EmojiPickerV3 } from "@circle-react-shared/uikit/EmojiPickerV3";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import {
  getCurrentRange,
  showSlashCommandMenu,
} from "@circle-react-shared/uikit/TipTapBlockEditor/Toolbar/helpers";
import { Portal } from "@circle-react-uikit/Portal";
import type { Editor } from "@tiptap/react";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";

export interface CommonHandlerProps {
  editor: Editor;
  range: {
    from: number;
    to: number;
  };
}
interface TiptapToolbarProps {
  disabledExtensions: any;
  editor: Editor;
  portalId: string;
  showFileUploadModal: (
    props: CommonHandlerProps & {
      type?: "file" | "video" | "audio";
    },
  ) => void;
  showGiphyPickerModal: (props: CommonHandlerProps) => void;
  showImagePickerModal: (props: CommonHandlerProps) => void;
  showPollModal: (props: CommonHandlerProps) => void;
  showVideoClipRecorder: (props: CommonHandlerProps) => void;
  showVoiceMessageModal: (props: CommonHandlerProps) => void;
}

export const Toolbar = ({
  editor,
  portalId,
  showImagePickerModal,
  showFileUploadModal,
  showGiphyPickerModal,
  showPollModal,
  disabledExtensions,
  showVideoClipRecorder,
  showVoiceMessageModal,
}: TiptapToolbarProps) => {
  if (!editor || !portalId) return null;
  return (
    <Portal selectorQuery={`#${portalId}`}>
      <ul className="flex list-none items-center gap-1.5">
        <li>
          <TippyV2
            placement="top"
            interactive={false}
            content={t("tiptap.open_slash_commands_menu")}
            dataTestId="open_slash_commands_menu"
          >
            <IconButton
              iconSize={20}
              iconClassName="!text-dark"
              name="20-add-circle"
              ariaLabel={t("tiptap.open_slash_commands_menu")}
              onClick={() => {
                showSlashCommandMenu({ editor });
              }}
            />
          </TippyV2>
        </li>
        <li>
          {!disabledExtensions["entities"] && (
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.add_entity")}
              dataTestId="add_entity_list"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                ariaLabel={t("tiptap.add_entity")}
                name="20-tiptap-entity"
                onClick={() => {
                  editor.chain().focus().insertContent(" #").run();
                }}
              />
            </TippyV2>
          )}
        </li>
        {!disabledExtensions["file_loader"] && (
          <li className="hidden md:flex">
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.attach_file")}
              dataTestId="attach_file"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                ariaLabel={t("tiptap.attach_file")}
                name="20-attach"
                onClick={() =>
                  showFileUploadModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </TippyV2>
          </li>
        )}
        {!disabledExtensions["file_loader"] && (
          <li>
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.attach_video")}
              dataTestId="attach_video"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                name="20-video"
                ariaLabel={t("tiptap.attach_video")}
                onClick={() =>
                  showFileUploadModal({
                    editor,
                    range: getCurrentRange({ editor }),
                    type: "video",
                  })
                }
              />
            </TippyV2>
          </li>
        )}
        {!disabledExtensions["file_loader"] && (
          <li className="hidden md:flex">
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.add_gif")}
              dataTestId="add_gif"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                name="20-gif"
                ariaLabel={t("tiptap.add_gif")}
                onClick={() =>
                  showGiphyPickerModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </TippyV2>
          </li>
        )}
        {!disabledExtensions["image"] && (
          <li>
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.add_image")}
              dataTestId="add_image"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                ariaLabel={t("tiptap.add_image")}
                onClick={() =>
                  showImagePickerModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
                name="20-file-image"
              />
            </TippyV2>
          </li>
        )}
        {!disabledExtensions["emojipicker"] && (
          <TippyV2
            placement="top"
            interactive={false}
            content={t("tiptap.add_emoji")}
            dataTestId="add_emoji"
          >
            <EmojiPickerV3
              placement="top-start"
              onEmojiSelect={emojiData => {
                if (!emojiData.emoji) return;
                editor.chain().focus().insertContent(emojiData.emoji).run();
              }}
              as="li"
              shouldEmojiPickerRenderAsPortal
              targetWrapperClassName="!flex"
              target={
                <IconButton
                  iconSize={20}
                  ariaLabel={t("tiptap.add_emoji")}
                  iconClassName="!text-dark"
                  name="20-emoji"
                />
              }
            />
          </TippyV2>
        )}
        {!disabledExtensions["poll"] && (
          <li>
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.add_poll")}
              dataTestId="add_poll"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                name="20-editor-poll"
                ariaLabel={t("tiptap.add_poll")}
                onClick={() =>
                  showPollModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </TippyV2>
          </li>
        )}
        {!disabledExtensions["voice_message"] && (
          <li>
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.record_voice_message")}
              dataTestId="record_voice_message"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                name="20-microphone"
                ariaLabel={t("tiptap.record_voice_message")}
                onClick={() =>
                  showVoiceMessageModal({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </TippyV2>
          </li>
        )}
        {!disabledExtensions["video_clip"] && (
          <li>
            <TippyV2
              placement="top"
              interactive={false}
              content={t("tiptap.record_video_clip")}
              dataTestId="record_video_clip_toolbar"
            >
              <IconButton
                iconSize={20}
                iconClassName="!text-dark"
                name="20-camera"
                ariaLabel={t("tiptap.record_video_clip")}
                onClick={() =>
                  showVideoClipRecorder({
                    editor,
                    range: getCurrentRange({ editor }),
                  })
                }
              />
            </TippyV2>
          </li>
        )}
      </ul>
    </Portal>
  );
};
